import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DsBoxModule,
  DsRowData,
  DsIconModule,
  DsButtonModule,
  DsLinkModule,
  DsSkeletonModule,
  DsFormsModule,
} from '@bmw-ds/components';
import { ICDQuartzTheme } from '../../shared/ag-theme';
import { ApiService } from 'src/app/services/api.service';
import { Router } from '@angular/router';
import {
  DsLoadingAreaConfiguration,
  DsLoadingAreaService,
} from '@bmw-ds/components';
import { ViewDataRendererComponent } from '../../shared/view-data-renderer/view-data-renderer';
import { CampaignInventoryInternalSearch } from '../campaign_inventory_internal_search/campaign-inventory-internal-search/campaign-inventory-internal-search';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import {
  GridApi,
  GridReadyEvent,
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
  Theme,
} from 'ag-grid-community';
import { CommonService } from 'src/app/services/common.service';
import { ExportService } from 'src/app/services/export.service';
ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-campaign-inventory-internal-national',
  standalone: true,
  imports: [
    CommonModule,
    DsBoxModule,
    DsIconModule,
    CampaignInventoryInternalSearch,
    AgGridModule,
    AgGridAngular,
    DsButtonModule,
    DsLinkModule,
    DsSkeletonModule,
    DsFormsModule,
  ],
  templateUrl: './campaign-inventory-internal-national.html',
  styleUrl: './campaign-inventory-internal-national.scss',
})
export class CampaignInventoryInternalNational implements OnInit {
  theme: Theme | 'legacy' = ICDQuartzTheme;
  noRowsTemplate = '<p> No Rows Found </p>';
  paginationPageSize: number = 10;
  paginationPageSizeSelector: number[] | boolean = [10, 20, 30];
  rowData: DsRowData[] = [];
  private gridApi!: GridApi;
  dynamicTableHeight: number = 550; // Default min height
  private readonly ROW_HEIGHT = 40;
  private readonly HEADER_HEIGHT = 50;
  private readonly FLOATING_FILTER_HEIGHT = 30;
  private readonly PAGINATION_PANEL_HEIGHT = 48;
  private readonly MAX_VISIBLE_ROWS = 20; // Maximum rows to display without vertical scroll
  nationalTableData: any;
  selectedValue: any;
  loadingAreaConfig: DsLoadingAreaConfiguration = {
    id: 'bodyLoading',
    loadingTitle: 'Details are being displayed, this may take few seconds..',
    loadingText:
      'Please do not close the browser window. wait while we load data.',
  };
  userEnteredDefectCode: string = '';
  constructor(
    private route: Router,
    private apiService: ApiService,
    private router: Router,
    private loadingAreaService: DsLoadingAreaService,
    private commonService: CommonService,
    private exportService: ExportService
  ) {}

  ngOnInit() {
    this.rowData = [];
    this.commonService.defectCode.subscribe((value) => {
      this.userEnteredDefectCode = value;
      // this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
      this.rowData = [];
      this.commonService.defectCodeTableData.subscribe({
        next: (data: any) => {
          this.nationalTableData = data;
          // console.log('National Table Data:', this.nationalTableData);
          this.rowData = this.nationalTableData.map((row: any) => ({
            Region: row['region'] ?? null,
            VehiclesForWhichAllCampaignsCanBeCompleted:
              row['allCampaignCompleted'] ?? null,
            VehiclesForWhichSomeCampaignsCanBeCompleted:
              row['someCampaignCompleted'] ?? null,
            AllVehiclesInInventory: row['allVehicles'] ?? null,
          }));
          this.calculateDynamicHeight();
        },
        error: (error: any) => {
          console.error('Error fetching National Table Data:', error);
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id); // Removes the loading area
        },
        complete: () => {
          console.log('National Table Data fetch completed');
          // this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id); // Removes the loading area
        },
      });
    });
  }

  onDataViewClick(params: any) {
    // console.log('View Data Clicked', params);
    this.selectedValue = params.value ?? '';
    // console.log('Selected Value from national:', this.selectedValue);
    this.commonService.regionalScreenSelectedValue.next(this.selectedValue);
    const columnName =
      params?.colDef?.field ?? params?.column?.getColId?.() ?? '';
    this.router.navigate(['campaign_inventory_internal_region'], {
      queryParams: {
        column: columnName,
        value: this.selectedValue,
        defectCode: params.data?.DefectCode ?? null,
      },
    });
  }

  windowPrint() {
    const columns = [
      'Region',
      'VehiclesForWhichAllCampaignsCanBeCompleted',
      'VehiclesForWhichSomeCampaignsCanBeCompleted',
      'AllVehiclesInInventory',
    ];
    const columnHeaders = [
      'Region',
      'Vehicles for which all Campaign(s) can be completed',
      'Vehicles for which some Campaign(s) can be completed',
      'All vehicles in Inventory',
    ];
    const currentDate = new Date().toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const currentTime = new Date().toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
    });

    const details: Array<{ label: string; value: any }> = [
      { label: 'Defect Code', value: this.userEnteredDefectCode || '' },
      { label: 'Details as of', value: currentDate + ' at ' + currentTime },
    ];

    const printedOn = this.exportService.formatEstTimestamp();

    this.exportService.exportPdfWithDetails(
      'Inventory_Campaign_Details_National',
      this.rowData,
      columns,
      columnHeaders,
      details,
      {
        title: 'ROSS - Inventory Campaign',
        subtitle: 'Inventory Campaign Details (Internal)-National',
        printedOn,
        orientation: 'landscape',
        rowHeight: 28,
        cellPadding: 5,
        columnWidths: [60, 120, 120, 80],
        zebraFillColor: [245, 245, 245],
        headerFillColor: [227, 242, 253],
      }
    );
  }

  columns: ColDef[] = [
    {
      field: 'Region',
      headerName: 'Region',
      flex: 1,
      cellRenderer: ViewDataRendererComponent,
      cellRendererParams: {
        onClick: this.onDataViewClick.bind(this),
      },
    },
    {
      field: 'VehiclesForWhichAllCampaignsCanBeCompleted',
      headerName: 'Vehicles for which all Campaign(s) can be completed',
      sortable: true,
      flex: 1,
    },
    {
      field: 'VehiclesForWhichSomeCampaignsCanBeCompleted',
      headerName: 'Vehicles for which some Campaign(s) can be completed',
      sortable: true,
      flex: 1,
    },
    {
      field: 'AllVehiclesInInventory',
      headerName: 'All vehicles in Inventory',
      sortable: true,
      flex: 1,
    },
  ];

  defaultColDef: ColDef = {
    flex: 1,
    // minWidth: 100,
    resizable: true,
    wrapHeaderText: true,
    autoHeight: true,
    headerCheckboxSelectionFilteredOnly: true,
    filterParams: {
      closeOnApply: true,
      suppressAndOrCondition: true,
      maxNumConditions: 1,
      trimInput: false,
      filterOptions: [
        // ? explicitly list all filter options you want
        'contains',
      ],
      defaultOption: 'contains',
    },
  };

  gridOptions: GridOptions = {
    suppressRowClickSelection: true,
    rowSelection: 'multiple',
    animateRows: true,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    floatingFiltersHeight: 30,
    suppressHorizontalScroll: true,
  };

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    // console.log('Grid API:', this.gridApi);
    this.fitGridColumns();
    this.calculateDynamicHeight();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.fitGridColumns();
  }

  onPaginationChanged() {
    this.calculateDynamicHeight();
  }

  onFilterChanged() {
    this.calculateDynamicHeight();
  }

  private calculateDynamicHeight() {
    if (!this.gridApi) return;

    const displayedRowCount = this.gridApi.getDisplayedRowCount();
    const currentPage = this.gridApi.paginationGetCurrentPage();
    const pageSize = this.gridApi.paginationGetPageSize();
    const startIndex = currentPage * pageSize;
    const visibleRowsOnCurrentPage = Math.max(
      Math.min(displayedRowCount - startIndex, pageSize),
      0
    );
    const cappedRowCount = Math.min(
      Math.max(visibleRowsOnCurrentPage, 1),
      this.MAX_VISIBLE_ROWS
    );

    const chromeHeight =
      this.HEADER_HEIGHT +
      this.FLOATING_FILTER_HEIGHT +
      this.PAGINATION_PANEL_HEIGHT;

    // Keep a tiny buffer so bottom borders are not clipped.
    this.dynamicTableHeight = chromeHeight + cappedRowCount * this.ROW_HEIGHT;
  }

  private fitGridColumns() {
    if (!this.gridApi) return;

    // Defer fitting until layout settles after viewport changes.
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    }, 0);
  }

  onRowClicked(event: any) {
    // console.log('Row clicked: ', event.data);
  }

  firstColumnNavigation() {
    if (this.gridApi) {
      const selectedRows = this.gridApi.getSelectedRows();
      // console.log('Selected option :', selectedRows);
      const selectedFirstColumn = selectedRows.map((row) => {
        return row?.[Object.keys(row)[0]];
      });
      // console.log('Confirm Not in Inventory Rows:', selectedFirstColumn);
    }
  }
}
